import { Error } from '@atom-learning/icons'
import * as React from 'react'
import { useToaster } from 'react-hot-toast'

import { styled } from '~/styled'

import { Spacer } from '../spacer/Spacer'
import { Text } from '../text/Text'
import { Toast } from './Toast'
import { ToastContext } from './ToastContext'

export { default as toast } from 'react-hot-toast'
export { useToastContext } from './ToastContext'

const ToastProviderBase = styled('div', {
  base: ['fixed', 'z-2147483647', 'inset-2', 'pointer-events-none', 'sm:top-3']
})

const ToastWrapper = styled('div', {
  base: [
    'absolute',
    'w-full',
    'flex',
    'justify-center',
    'items-center',
    'rounded-sm',
    'box-border',
    'min-h-12',
    '[--slide-opacity-out:0]',
    '[--slide-direction-out-y:-100%]'
  ],
  variants: {
    visible: {
      true: ['motion-safe:animate-slide-direction-in'],
      false: ['opacity-0', 'motion-safe:animate-slide-direction-out']
    }
  }
})

export const ToastProvider = ({
  children,
  className
}: React.PropsWithChildren<{ className?: string }>) => {
  const { toasts, handlers } = useToaster()
  const { startPause, endPause, calculateOffset, updateHeight } = handlers

  return (
    <>
      <ToastProviderBase
        onMouseEnter={startPause}
        onMouseLeave={endPause}
        className={className}
      >
        {toasts.map((toast) => {
          const { message: children } = toast

          const offset = calculateOffset(toast, {
            reverseOrder: true,
            gutter: 8
          })

          const ref = (el: HTMLDivElement | null) => {
            if (el && toast.height === undefined) {
              updateHeight(toast.id, el.getBoundingClientRect().height)
            }
          }

          return (
            <ToastWrapper
              key={toast.id}
              ref={ref}
              visible={toast.visible}
              role={toast.ariaProps.role}
              aria-live={toast.ariaProps['aria-live']}
              style={{ '--offset': `${offset}px` }}
              className="top-(--offset)"
            >
              <ToastContext.Provider value={toast}>
                {typeof children === 'function' ? (
                  children(toast)
                ) : React.isValidElement(children) ? (
                  children
                ) : (
                  <Toast className="w-100">
                    {toast.type === 'error' && <Toast.Icon is={Error} />}
                    <Text>{children}</Text>
                    <Spacer />
                    <Toast.Close />
                  </Toast>
                )}
              </ToastContext.Provider>
            </ToastWrapper>
          )
        })}
      </ToastProviderBase>
      {children}
    </>
  )
}

ToastProvider.displayName = 'ToastProvider'
